import React from "react";
import { styled } from "styled-components";
import { AttrValue, SchemaField } from "./configTypes";
import { EMPTY_DISPLAY, formatAttr, optionFor } from "./configModel";
import { glyphChar, severityStyle } from "./severity";
import { tokens } from "./tokens";

/**
 * One stored value, rendered the way the schema says it should read.
 *
 * Enum values carry a severity and (in the capability matrix) a glyph, and both
 * are always shown together — the glyph is what makes the status legible
 * without relying on the red/green distinction. Non-enum values render as plain
 * text, so this is safe to point at any field.
 */
interface ValueChipProps {
  field: SchemaField;
  value: AttrValue;
  /** Suppress the pill treatment for values that read better as plain text. */
  plain?: boolean;
  /**
   * Let the text wrap instead of ellipsing at one line.
   *
   * A table cell must stay one line high, so there the truncation is the point.
   * Off the table — in the row expansion — it isn't: `notes` is prose, and
   * moving it out of the grid to make it readable only to clip it at the same
   * character would have achieved nothing.
   */
  wrap?: boolean;
}

const ValueChip: React.FC<ValueChipProps> = ({ field, value, plain, wrap }) => {
  const text = formatAttr(field, value);
  if (text === EMPTY_DISPLAY) return <Empty>{EMPTY_DISPLAY}</Empty>;

  // A list renders as the entries it holds, not as one comma-joined string:
  // the read view and the editor describe the same array, so they draw the
  // same pills, and "how many routes" is countable at a glance. Free to wrap —
  // no string_list field is ever a one-line table column (see COLUMN_FIELDS).
  if (field.type === "string_list" && Array.isArray(value)) {
    return (
      <ListValue>
        {value.map((entry, index) => (
          <ListEntry key={`${entry}-${index}`} title={entry}>
            {entry}
          </ListEntry>
        ))}
      </ListValue>
    );
  }

  const option = optionFor(field, value);
  if (!option || plain) {
    return (
      <Plain title={text} $wrap={wrap}>
        {text}
      </Plain>
    );
  }

  const style = severityStyle(option.severity);
  return (
    <Chip
      title={option.help ?? option.label}
      style={{
        background: style.bg,
        color: style.color,
        borderColor: style.border,
      }}
    >
      {option.glyph && (
        <Glyph aria-hidden="true">{glyphChar(option.glyph)}</Glyph>
      )}
      {option.label}
    </Chip>
  );
};

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  max-width: 100%;
`;

const Glyph = styled.span`
  font-size: 11px;
  line-height: 1;
`;

/**
 * The pill one list entry sits in. Shared with the editor's removable variant
 * (FieldControl's `Entry`), so an array reads identically whether it is being
 * shown or changed — entering Edit adds the remove affordance and restyles
 * nothing.
 */
export const listEntryBase = `
  max-width: 100%;
  background: ${tokens.offWhite};
  border: 1px solid ${tokens.line};
  border-radius: 6px;
  font-size: 13px;
`;

const ListValue = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
`;

const ListEntry = styled.span`
  ${listEntryBase}
  display: inline-block;
  padding: 3px 9px;
  color: ${tokens.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Plain = styled.span<{ $wrap?: boolean }>`
  font-size: 13px;
  color: ${tokens.ink};
  max-width: 100%;
  ${(p) =>
    p.$wrap
      ? `white-space: pre-wrap; overflow-wrap: anywhere;`
      : `overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}
`;

// Slate, not toggleOff: "nothing recorded" is an answer the reader has to be
// able to see, and the lighter grey sat at 1.6:1 against the card.
const Empty = styled.span`
  font-size: 13px;
  color: ${tokens.slate};
`;

export default ValueChip;
